import type { JSX } from "react";
import { Icon, Text, View } from "@/components";
import type { FaqBlock, FaqItem, FaqListItem } from "@/types/elearning/landing";

export interface FaqAccordionProps {
  items: FaqItem[];
}

/** Clave estable de un bloque de respuesta (los bloques no tienen id propio). */
const blockKey = (block: FaqBlock): string =>
  block.kind === "paragraph" ? block.text : (block.items[0]?.text ?? "list");

const FaqAnswerList = ({ items }: { items: FaqListItem[] }): JSX.Element => (
  <View type="ul" className="flex flex-col gap-2 pl-5 list-disc">
    {items.map((item) => (
      <View
        key={item.text}
        type="li"
        className="text-sm text-foreground/80 leading-relaxed"
      >
        {item.text}
        {item.children && (
          <View
            type="ul"
            className="mt-2 flex flex-col gap-2 pl-5 list-[circle]"
          >
            {item.children.map((child) => (
              <View key={child} type="li">
                {child}
              </View>
            ))}
          </View>
        )}
      </View>
    ))}
  </View>
);

/**
 * @name FaqAccordion
 * @description Acordeón de preguntas frecuentes de la landing pública, con
 * `details`/`summary` nativos: expande y colapsa sin JavaScript, accesible por
 * teclado y con el contenido completo en el HTML (indexable). Cada respuesta
 * se compone de bloques (párrafos y listas con sub-ítems).
 *
 * @param {FaqAccordionProps} props - Las preguntas frecuentes a renderizar.
 * @returns {JSX.Element} El acordeón renderizado.
 */
const FaqAccordion = ({ items }: FaqAccordionProps): JSX.Element => (
  <View className="flex flex-col gap-3">
    {items.map((item) => (
      <View
        key={item.id}
        id={item.id}
        type="details"
        className="group bg-card border border-border rounded-xl"
      >
        <View
          type="summary"
          className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-4 p-4"
        >
          <Text type="span" className="font-medium text-foreground">
            {item.question}
          </Text>
          <Icon
            name="chevron-down"
            className="text-muted-foreground shrink-0 transition-transform group-open:rotate-180"
          />
        </View>
        <View className="px-4 pb-4 flex flex-col gap-3">
          {item.answer.map((block) =>
            block.kind === "paragraph" ? (
              <Text
                key={blockKey(block)}
                type="p"
                className="text-sm text-foreground/80 leading-relaxed"
              >
                {block.text}
              </Text>
            ) : (
              <FaqAnswerList key={blockKey(block)} items={block.items} />
            )
          )}
        </View>
      </View>
    ))}
  </View>
);

export default FaqAccordion;
